/**
 * X / Instagram handle normalisation, client side (APS-255 / KGH-795).
 *
 * A DELIBERATE MIRROR of backend utils/socialHandles.js, which is the authority
 * — the server re-normalises and re-validates everything this file returns, and
 * migration 042's CHECK constraint sits behind that. This copy exists for one
 * reason: the family should see "@ is not needed, we took it off" and "that is
 * an Instagram link, not an X one" under the input as they type, not as a red
 * toast after a round trip. If the two ever disagree, the server wins and the
 * fix belongs in both.
 *
 *   x_handle          ~ ^[A-Za-z0-9_]{1,15}$
 *   instagram_handle  ~ ^[A-Za-z0-9_.]{1,30}$
 */

export type SocialNetwork = 'x' | 'instagram';

interface NetworkSpec {
  label: string;
  article: string;
  re: RegExp;
  hosts: string[];
  rule: string;
}

const NETWORKS: Record<SocialNetwork, NetworkSpec> = {
  x: {
    label: 'X',
    article: 'an X',
    re: /^[A-Za-z0-9_]{1,15}$/,
    hosts: ['x.com', 'twitter.com', 'mobile.twitter.com', 'www.x.com', 'www.twitter.com'],
    rule: 'letters, numbers and underscores, up to 15 characters',
  },
  instagram: {
    label: 'Instagram',
    article: 'an Instagram',
    re: /^[A-Za-z0-9_.]{1,30}$/,
    hosts: ['instagram.com', 'www.instagram.com', 'instagr.am'],
    rule: 'letters, numbers, periods and underscores, up to 30 characters',
  },
};

const KNOWN_HOSTS = new Set(Object.values(NETWORKS).flatMap((n) => n.hosts));

const RESERVED_PATHS = new Set([
  'p', 'reel', 'reels', 'stories', 'explore', 'accounts', 'direct', 'tv', 'about',
  'i', 'home', 'search', 'intent', 'share', 'hashtag', 'settings', 'login', 'signup',
]);

export interface NormalizedHandle {
  /** The canonical handle, or null when the field was cleared. */
  handle: string | null;
  /** A sentence to render under the input. Absent when the value is fine. */
  error?: string;
}

/**
 * Turn what someone typed into what we store.
 *
 * Accepts a pasted profile URL, because pasting the page is what people do. The
 * URL test is narrow on purpose — a scheme, a slash, or a host we know — since
 * "sarah.pitches" is both a perfectly ordinary Instagram handle and something
 * that looks exactly like a domain.
 */
export function normalizeSocialHandle(raw: string | null | undefined, network: SocialNetwork): NormalizedHandle {
  const spec = NETWORKS[network];
  if (raw === undefined || raw === null) return { handle: null };

  let value = String(raw).trim();
  if (!value) return { handle: null };

  const withoutScheme = value.replace(/^https?:\/\//i, '');
  const hadScheme = withoutScheme !== value;
  const hostCandidate = withoutScheme.split('/')[0].toLowerCase();
  const hasPath = withoutScheme.includes('/');

  if (hadScheme || hasPath || KNOWN_HOSTS.has(hostCandidate)) {
    if (!spec.hosts.includes(hostCandidate)) {
      return { handle: null, error: `That link is not ${spec.article} profile. Paste your ${spec.label} profile link, or just the handle.` };
    }
    const path = withoutScheme.slice(hostCandidate.length).split(/[?#]/)[0];
    const segments = path.split('/').filter(Boolean);
    if (segments.length === 0) {
      return { handle: null, error: `That link points at ${spec.label}, not at a profile. Paste your own profile link, or just the handle.` };
    }
    if (RESERVED_PATHS.has(segments[0].toLowerCase().replace(/^@+/, ''))) {
      return { handle: null, error: 'That link points at a post, not at a profile. Paste your own profile link, or just the handle.' };
    }
    value = segments[0];
  }

  value = value.replace(/\s+/g, '').replace(/^@+/, '').replace(/\/+$/, '');
  if (!value) return { handle: null };

  if (!spec.re.test(value)) {
    return { handle: null, error: `"${String(raw).trim()}" is not ${spec.article} handle. Use ${spec.rule}.` };
  }
  return { handle: value };
}

/** The public profile URL for a stored handle. No handle, no URL. */
export function socialUrl(handle: string | null | undefined, network: SocialNetwork): string | null {
  if (!handle) return null;
  return network === 'instagram' ? `https://instagram.com/${handle}` : `https://x.com/${handle}`;
}
